import { withoutBody } from "@/lib/blog";
import type { GetStaticPaths, GetStaticProps } from "next";
import Layout from "@/components/Layout";
import PageTitle from "@/components/PageTitle";
import BlogCard from "@/components/BlogCard";
import { getPosts } from "@/lib/cms";
import { slugifyCategory, type BlogPost } from "@/lib/blog";

// Preserves $route['blog-category/(:any)'] from the CodeIgniter routes file, so
// /blog/blog-category/<name> keeps working and its backlinks stay valid.

type Props = { posts: BlogPost[]; category: string };

export default function BlogCategory({ posts, category }: Props) {
  return (
    <Layout
      route={`/blog/blog-category/${slugifyCategory(category)}`}
      seo={{
        title: `${category} Articles | Chest Surgery India`,
        description: `Articles on ${category} from Dr. Parveen Yadav and the Chest Surgery India team.`,
        canonical: `/blog/blog-category/${slugifyCategory(category)}`,
      }}
    >
      <PageTitle title={category} crumb={category} />
      <section className="blog-area-two pt-100">
        <div className="container">
          <div className="row">
            {posts.map((post) => (
              <BlogCard key={post.slug} post={post} />
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
}

export const getStaticPaths: GetStaticPaths = async () => {
  const posts = await getPosts();
  const categories = new Set(posts.map((p) => p.category).filter(Boolean));
  return {
    paths: [...categories].map((c) => ({ params: { category: slugifyCategory(c) } })),
    fallback: false,
  };
};

export const getStaticProps: GetStaticProps<Props, { category: string }> = async ({ params }) => {
  const all = await getPosts();
  const match = all.find((p) => slugifyCategory(p.category) === params!.category);
  if (!match) return { notFound: true };

  return {
    props: {
      category: match.category,
      posts: all.filter((p) => slugifyCategory(p.category) === params!.category).map(withoutBody),
    },
  };
};
